// PWA installation detection and prompt utilities

export interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallState {
    isInstalled: boolean;
    isInstallable: boolean;
    platform: 'ios' | 'android' | 'desktop' | 'unknown';
    browser: 'safari' | 'chrome' | 'firefox' | 'edge' | 'samsung' | 'unknown';
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;

function detectPlatform(): 'ios' | 'android' | 'desktop' | 'unknown' {
    const ua = navigator.userAgent.toLowerCase();
    
    if (/iphone|ipad|ipod/.test(ua)) {
        return 'ios';
    }
    if (/android/.test(ua)) {
        return 'android';
    }
    if (/windows|macintosh|linux/.test(ua) && !/mobile/.test(ua)) {
        return 'desktop';
    }
    return 'unknown';
}

function detectBrowser(): 'safari' | 'chrome' | 'firefox' | 'edge' | 'samsung' | 'unknown' {
    const ua = navigator.userAgent.toLowerCase();
    
    // Order matters - check more specific browsers first
    if (/samsungbrowser/.test(ua)) {
        return 'samsung';
    }
    if (/edg/.test(ua)) {
        return 'edge';
    }
    if (/chrome/.test(ua) && !/edg/.test(ua)) {
        return 'chrome';
    }
    if (/safari/.test(ua) && !/chrome/.test(ua)) {
        return 'safari';
    }
    if (/firefox/.test(ua)) {
        return 'firefox';
    }
    return 'unknown';
}

function isStandalone(): boolean {
    // Check various ways the app might be running in standalone mode
    const nav = navigator as Navigator & { standalone?: boolean };
    
    // iOS Safari standalone mode
    if (nav.standalone === true) {
        return true;
    }
    
    // Standard display-mode media query
    if (window.matchMedia('(display-mode: standalone)').matches) {
        return true;
    }
    
    // Windows PWA
    if (window.matchMedia('(display-mode: window-controls-overlay)').matches) {
        return true;
    }
    
    // Fallback: check if running in minimal-ui
    if (window.matchMedia('(display-mode: minimal-ui)').matches) {
        return true;
    }
    
    return false;
}

export function getInstallState(): InstallState {
    return {
        isInstalled: isStandalone(),
        isInstallable: deferredPrompt !== null,
        platform: detectPlatform(),
        browser: detectBrowser()
    };
}

export function setupInstallPrompt(): void {
    // Capture the beforeinstallprompt event (Chrome/Edge/Samsung)
    window.addEventListener('beforeinstallprompt', (e: Event) => {
        console.log('[PWA] beforeinstallprompt fired');
        e.preventDefault();
        deferredPrompt = e as BeforeInstallPromptEvent;
        
        // Notify Blazor that install is available
        notifyInstallStateChanged();
    });

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
        console.log('[PWA] App was installed');
        deferredPrompt = null;
        notifyInstallStateChanged();
    });
}

export async function triggerInstallPrompt(): Promise<boolean> {
    if (!deferredPrompt) {
        console.log('[PWA] No install prompt available');
        return false;
    }

    try {
        await deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        
        if (choice.outcome === 'accepted') {
            console.log('[PWA] User accepted install prompt');
            deferredPrompt = null;
            return true;
        } else {
            console.log('[PWA] User dismissed install prompt');
            return false;
        }
    } catch (error) {
        console.error('[PWA] Install prompt failed:', error);
        return false;
    }
}

function notifyInstallStateChanged(): void {
    // Dispatch custom event that Blazor can listen for
    window.dispatchEvent(new CustomEvent('pwainstallstatechanged', {
        detail: getInstallState()
    }));
}

// Check if we should skip the install prompt (for testing/development)
export function shouldShowInstallPrompt(): boolean {
    // Allow bypass via URL parameter for development
    const params = new URLSearchParams(window.location.search);
    if (params.has('skipInstallCheck')) {
        return false;
    }
    
    return !isStandalone();
}
